import { Component, type ErrorInfo, type ReactNode } from 'react';
import errorTracker from '../../services/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  retryCount: number;
}

/**
 * Global Error Boundary
 *
 * Catches all unhandled React errors and provides user-friendly fallback UI
 * with error reporting and recovery options.
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Track error with comprehensive context
    const errorId = errorTracker.captureComponentError(
      error,
      'GlobalErrorBoundary',
      'react_error_boundary',
      {
        additionalData: {
          componentStack: errorInfo.componentStack,
          errorBoundary: 'GlobalErrorBoundary',
          retryCount: this.state.retryCount,
          errorInfo,
        },
      },
    );

    this.setState({ errorId });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log additional context for debugging
    console.group('🚨 Global Error Boundary - React Error Caught');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.info('Error ID:', errorId);
    console.info('Retry Count:', this.state.retryCount);
    console.groupEnd();
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState((prevState) => ({
        hasError: false,
        error: null,
        errorId: null,
        retryCount: prevState.retryCount + 1,
      }));

      // Track retry attempt
      errorTracker.captureComponentError(
        new Error('User initiated retry'),
        'GlobalErrorBoundary',
        'retry_attempt',
        {
          additionalData: {
            retryCount: this.state.retryCount + 1,
            originalErrorId: this.state.errorId,
          },
        },
      );
    }
  };

  handleReload = () => {
    // Track reload attempt
    errorTracker.captureComponentError(
      new Error('User initiated page reload'),
      'GlobalErrorBoundary',
      'page_reload',
      {
        additionalData: {
          retryCount: this.state.retryCount,
          originalErrorId: this.state.errorId,
        },
      },
    );

    window.location.reload();
  };

  handleReportError = () => {
    if (this.state.error && this.state.errorId) {
      // Create a detailed error report for user submission
      const _errorReport = {
        errorId: this.state.errorId,
        message: this.state.error.message,
        stack: this.state.error.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        retryCount: this.state.retryCount,
      };

      // In a real app, this would open a support ticket or email
      const mailtoLink = `mailto:support@sangoma.com?subject=Error Report - ${this.state.errorId}&body=${encodeURIComponent(
        `Error Report\n\nError ID: ${this.state.errorId}\nMessage: ${this.state.error.message}\nURL: ${window.location.href}\nTimestamp: ${new Date().toISOString()}\n\nPlease describe what you were doing when this error occurred:\n\n`,
      )}`;

      window.open(mailtoLink);

      // Track error report submission
      errorTracker.captureComponentError(
        new Error('User submitted error report'),
        'GlobalErrorBoundary',
        'error_report_submitted',
        {
          additionalData: {
            originalErrorId: this.state.errorId,
          },
        },
      );
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default comprehensive error UI
      return (
        <div className="global-error-boundary">
          <div className="error-container">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>

            <div className="error-content">
              <h1 className="error-title">Something went wrong</h1>
              <p className="error-message">
                We're sorry, but something unexpected happened. The error has been automatically
                reported to our team.
              </p>

              {this.state.error && (
                <details className="error-details">
                  <summary>Technical Details</summary>
                  <div className="error-technical">
                    <p>
                      <strong>Error:</strong> {this.state.error.message}
                    </p>
                    <p>
                      <strong>Error ID:</strong> {this.state.errorId}
                    </p>
                    <p>
                      <strong>Time:</strong> {new Date().toLocaleString()}
                    </p>
                    {this.state.error.stack && (
                      <pre className="error-stack">{this.state.error.stack}</pre>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="error-actions">
              {this.state.retryCount < this.maxRetries && (
                <button type="button" onClick={this.handleRetry} className="btn btn-primary">
                  Try Again ({this.maxRetries - this.state.retryCount} attempts left)
                </button>
              )}

              <button type="button" onClick={this.handleReload} className="btn btn-secondary">
                Reload Page
              </button>

              <button type="button" onClick={this.handleReportError} className="btn btn-outline">
                Report Error
              </button>
            </div>

            {this.state.retryCount > 0 && (
              <div className="retry-info">
                <p>
                  Retry attempts: {this.state.retryCount}/{this.maxRetries}
                </p>
              </div>
            )}

            <div className="error-help">
              <p>
                If this problem persists, please contact support at{' '}
                <a href="mailto:support@sangoma.com">support@sangoma.com</a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
